import type { RouteLocationRaw } from "vue-router";

export type InboxNotificationLinkTarget
  = | { type: "task", id: number }
    | { type: "report", id: number }
    | { type: "reservation", id: number }
    | { type: "app_subscription", clientId: number, id: number }
    | { type: "quiet_hours_summary", channel: number, windowEndsAt: string };

/**
 * Query parameters that open the quiet-hours summary dialog on any signed-in screen. Web push clicks and the summary
 * e-mail open `/t?quietHoursChannel=<channel>&quietHoursWindowEndsAt=<window end, ISO 8601>`.
 */
export const QUIET_HOURS_CHANNEL_QUERY_PARAM = "quietHoursChannel";
export const QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM = "quietHoursWindowEndsAt";

/** The summary target from its two parts, or null when the channel is not a number or the window end is missing. */
export function quietHoursSummaryTarget(channel: unknown, windowEndsAt: unknown): InboxNotificationLinkTarget | null {
  const channelNumber = typeof channel === "string" ? Number.parseInt(channel, 10) : channel;
  if (typeof channelNumber !== "number" || !Number.isInteger(channelNumber) || typeof windowEndsAt !== "string" || !windowEndsAt) {
    return null;
  }
  return { type: "quiet_hours_summary", channel: channelNumber, windowEndsAt };
}

// App links produced by the API, e.g. `verbleif://TaskEdit/12`, `verbleif://ReportEdit/7/Activities`,
// `verbleif://Reservation/30` or `verbleif://AppStore/3/45` (client 3, install 45).
const APP_LINK_PATTERN = /^verbleif:\/\/(TaskEdit|ReportEdit|Reservation)\/(\d+)(?:[/?#]|$)/;
const APP_LINK_TYPES = { TaskEdit: "task", ReportEdit: "report", Reservation: "reservation" } as const;
const APP_STORE_LINK_PATTERN = /^verbleif:\/\/AppStore\/(\d+)\/(\d+)(?:[/?#]|$)/;
// The quiet-hours summary push: `verbleif://inbox/quiet-hours-summary?windowEndsAt=<ISO>&channel=<n>`.
const QUIET_HOURS_SUMMARY_LINK_PREFIX = "verbleif://inbox/quiet-hours-summary?";

export function parseInboxNotificationLink(link: string | null | undefined): InboxNotificationLinkTarget | null {
  if (!link) {
    return null;
  }

  if (link.startsWith(QUIET_HOURS_SUMMARY_LINK_PREFIX)) {
    const query = new URLSearchParams(link.slice(QUIET_HOURS_SUMMARY_LINK_PREFIX.length));
    return quietHoursSummaryTarget(query.get("channel"), query.get("windowEndsAt"));
  }

  const appStoreMatch = link.match(APP_STORE_LINK_PATTERN);
  if (appStoreMatch) {
    return {
      type: "app_subscription",
      clientId: Number(appStoreMatch[1]),
      id: Number(appStoreMatch[2]),
    };
  }

  const match = link.match(APP_LINK_PATTERN);
  if (!match) {
    return null;
  }

  return {
    type: APP_LINK_TYPES[match[1] as keyof typeof APP_LINK_TYPES],
    id: Number(match[2]),
  };
}

// Web URLs of the same screens, as the API builds them for e-mails and web pushes (NotificationWebLink).
const WEB_TASK_PATH_PATTERN = /^\/t\/li\/[^/]+\/t(\d+)$/;
const WEB_REPORT_PATH_PATTERN = /^\/r\/li\/[^/]+\/r(\d+)$/;
const WEB_RESERVATION_PATH_PATTERN = /^\/res\/li\/[^/]+\/(\d+)$/;
const WEB_APP_STORE_PATH_PATTERN = /^\/app-store\/(\d+)\/(\d+)$/;

/**
 * The target of a web URL the API built (`https://<web frontend>/t/li/td/t55?notification=...`), or null for an app
 * link or a screen without a target here. Only the path and query count: the API's web frontend URL may differ from
 * this app's origin (local development, another host), and the target always opens in this app.
 */
export function parseWebNotificationLink(link: string | null | undefined): InboxNotificationLinkTarget | null {
  if (!link || !/^https?:\/\//.test(link)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.pathname === "/t" && url.searchParams.has(QUIET_HOURS_CHANNEL_QUERY_PARAM)) {
    return quietHoursSummaryTarget(url.searchParams.get(QUIET_HOURS_CHANNEL_QUERY_PARAM), url.searchParams.get(QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM));
  }
  const task = url.pathname.match(WEB_TASK_PATH_PATTERN);
  if (task) {
    return { type: "task", id: Number(task[1]) };
  }
  const report = url.pathname.match(WEB_REPORT_PATH_PATTERN);
  if (report) {
    return { type: "report", id: Number(report[1]) };
  }
  const reservation = url.pathname.match(WEB_RESERVATION_PATH_PATTERN);
  if (reservation) {
    return { type: "reservation", id: Number(reservation[1]) };
  }
  const appStore = url.pathname.match(WEB_APP_STORE_PATH_PATTERN);
  if (appStore) {
    return { type: "app_subscription", clientId: Number(appStore[1]), id: Number(appStore[2]) };
  }
  return null;
}

/**
 * The web screen of a notification link target: the task, report or reservation in the "all" list, the app
 * install, or the quiet-hours summary dialog over the current screen.
 */
export function inboxNotificationRoute(target: InboxNotificationLinkTarget): RouteLocationRaw {
  if (target.type === "quiet_hours_summary") {
    return {
      query: {
        [QUIET_HOURS_CHANNEL_QUERY_PARAM]: String(target.channel),
        [QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM]: target.windowEndsAt,
      },
    };
  }
  if (target.type === "app_subscription") {
    return { name: "app-store", params: { clientId: String(target.clientId), appSubscriptionId: String(target.id) } };
  }
  if (target.type === "reservation") {
    return { name: "reservation-list", params: { type: "all", reservationId: String(target.id) } };
  }
  if (target.type === "task") {
    return { name: "task-list", params: { taskId: String(target.id) }, query: { view: "all" } };
  }
  return { name: "report-list", params: { reportId: String(target.id) }, query: { view: "all" } };
}
